
import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import {
  ArrowLeft, Play, Pause, RotateCcw, RotateCw,
  Link as LinkIcon,
  MoreHorizontal,
  Loader2, Mic,
  Search, Globe
} from 'lucide-react';
import { TranscriptSegment, NoteBlock } from '../types';
import { useLanguage } from '../i18nContext';
import { getTaskResult, TaskResult, TranscriptItem, MeetingNoteItem, ParagraphMatchItem, updateSpeakerName, updateSingleSpeakerName, updateAppliedSchema, getGeneratedNote, getEditedNote } from '../services/task';
import { SchemaEditorWrapper } from './SchemaEditor/SchemaEditorWrapper';


// Helper to parse "MM:SS" to seconds
const parseTime = (timeStr: string) => {
  const parts = timeStr.split(':');
  if (parts.length === 2) {
    return parseInt(parts[0]) * 60 + parseInt(parts[1]);
  }
  return 0;
};

interface WorkspaceProps {
  taskId?: string | null;
  onBack: () => void;
  onOpenShare?: () => void;
}

export default function Workspace({ taskId, onBack, onOpenShare }: WorkspaceProps) {
  const { t, language, setLanguage } = useLanguage();

  // 数据加载状态
  const [dataLoading, setDataLoading] = useState(!!taskId);
  const [dataError, setDataError] = useState<string | null>(null);
  const [taskData, setTaskData] = useState<TaskResult | null>(null);
  // 保存完整的段落匹配数据用于计算相关性
  const [paragraphMatches, setParagraphMatches] = useState<ParagraphMatchItem[]>([]);
  // 来源类型：audio 或 text
  const [sourceType, setSourceType] = useState<string>('audio');
  // 模板中心模式
  const [isTemplateCenter, setIsTemplateCenter] = useState(false);
  // schema 版本号，用于模板中心"应用模板"后触发右侧预览重新加载
  const [schemaVersion, setSchemaVersion] = useState(0);
  const handleApplyTemplate = useCallback((type?: string, schemaId?: string) => {
    setSchemaVersion(v => v + 1);
    // 同步更新本地 taskData，使 Sidebar 下拉框立即反映已应用的模板
    if (type && schemaId) {
      setTaskData(prev => prev ? { ...prev, appliedSchemaType: type, appliedSchemaId: schemaId } : prev);
    }
    // 持久化模板选择到后端
    if (taskId && type && schemaId) {
      updateAppliedSchema(taskId, type, schemaId).catch(err =>
        console.error('Failed to save applied schema:', err)
      );
    }
  }, [taskId]);

  // Initialize state with empty data
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  const [notes, setNotes] = useState<NoteBlock[]>([]);
  const [originalNotes, setOriginalNotes] = useState<NoteBlock[]>([]);
  const [savedEditedNotes, setSavedEditedNotes] = useState<NoteBlock[] | null>(null);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');

  // 从后端获取任务结果
  useEffect(() => {
    if (!taskId) {
      return;
    }

    const fetchTaskResult = async () => {
      setDataLoading(true);
      setDataError(null);
      try {
        const result = await getTaskResult(taskId);
        setTaskData(result);

        // 构建段落匹配映射：utteranceIndex -> noteIndex[] (多对多)
        const utteranceToNoteMap = new Map<number, number[]>();
        const noteToUtteranceMap = new Map<number, number[]>();
        if (result.paragraphMatches && result.paragraphMatches.length > 0) {
          result.paragraphMatches.forEach(match => {
            if (match.matchedUtteranceIndex != null) {
              const noteList = utteranceToNoteMap.get(match.matchedUtteranceIndex) || [];
              noteList.push(match.paragraphIndex);
              utteranceToNoteMap.set(match.matchedUtteranceIndex, noteList);

              const uttList = noteToUtteranceMap.get(match.paragraphIndex) || [];
              uttList.push(match.matchedUtteranceIndex);
              noteToUtteranceMap.set(match.paragraphIndex, uttList);
            }
          });
        }

        // 转换 transcript 数据
        const convertedSegments: TranscriptSegment[] = result.transcript.map((item, index) => {
          const linkedNoteIndices = utteranceToNoteMap.get(index);
          const linkedNoteIndex = linkedNoteIndices?.[0];
          return {
            id: `t${index + 1}`,
            speaker: item.speaker || 'Speaker',
            timestamp: formatTimestamp(item.startTime),
            text: item.text,
            linkedNoteId: linkedNoteIndex !== undefined ? `n${linkedNoteIndex + 1}` : undefined
          };
        });
        setSegments(convertedSegments);

        // 转换 meetingNotes 数据
        const convertedNotes: NoteBlock[] = result.meetingNotes.map((item, index) => {
          const linkedUtteranceIndices = noteToUtteranceMap.get(index);
          const linkedUtteranceIndex = linkedUtteranceIndices?.[0];
          const level = item.level || (item.level === 1 ? 1 : item.level === 2 ? 2 : 3);
          return {
            id: `n${index + 1}`,
            level: level,
            type: item.level === 1 ? 'heading' : item.level === 2 ? 'bullet' : 'paragraph',
            content: item.content,
            linkedTranscriptId: linkedUtteranceIndex !== undefined ? `t${linkedUtteranceIndex + 1}` : undefined,
            originalParagraphIndex: index,
          };
        });
        setNotes(convertedNotes);

        // 设置标题和日期
        const taskTitle = result.title || (result.fileName ? result.fileName.replace(/\.[^/.]+$/, '') : '');
        setTitle(taskTitle);
        setDate(new Date().toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' }));

        // 设置音频URL
        if (result.audioUrl) {
          setAudioUrl(result.audioUrl);
        }

        // 设置来源类型
        if (result.sourceType) {
          setSourceType(result.sourceType);
        }

        // 保存段落匹配数据用于连线
        if (result.paragraphMatches) {
          setParagraphMatches(result.paragraphMatches);
        }

        // 获取原始生成版纪要（version_type='generated'），用于原始版显示和 notes 初始化
        try {
          const origItems = await getGeneratedNote(taskId);
          if (origItems.length > 0) {
            const origBlocks: NoteBlock[] = origItems.map((item, index) => {
              const level = item.level || 1;
              return {
                id: `n${index + 1}`,
                level,
                type: item.level === 1 ? 'heading' : item.level === 2 ? 'bullet' : 'paragraph',
                content: item.content,
                linkedTranscriptId: convertedNotes[index]?.linkedTranscriptId,
                originalParagraphIndex: index,
              };
            });
            setOriginalNotes(origBlocks);
            setNotes(origBlocks);
          } else {
            setOriginalNotes(convertedNotes);
          }
        } catch {
          // 获取原始版失败，用当前版本作为 fallback
          setOriginalNotes(convertedNotes);
        }

        // 获取后端保存的编辑版纪要
        try {
          const editedItems = await getEditedNote(taskId);
          if (editedItems.length > 0) {
            const editedBlocks: NoteBlock[] = editedItems.map((item: any, index: number) => ({
              id: item.id || `n${index + 1}`,
              level: item.level || 1,
              type: item.level === 1 ? 'heading' : item.level === 2 ? 'bullet' : 'paragraph',
              content: item.content,
            }));
            setSavedEditedNotes(editedBlocks);
          }
        } catch {
          // 无编辑版，忽略
        }

      } catch (e: any) {
        console.error('Failed to fetch task result:', e);
        setDataError(e.message || t.workspace.fetchFailed);
      } finally {
        setDataLoading(false);
      }
    };

    fetchTaskResult();
  }, [taskId, language]);

  // 格式化时间戳（毫秒 -> MM:SS）
  const formatTimestamp = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = Math.floor(totalSeconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const [activeLinkId, setActiveLinkId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  // Speaker Editing Modal State
  const [speakerModal, setSpeakerModal] = useState<{ segId: string; oldName: string } | null>(null);
  const [speakerModalName, setSpeakerModalName] = useState('');
  const [speakerModalAll, setSpeakerModalAll] = useState(false);
  const speakerModalInputRef = useRef<HTMLInputElement>(null);

  // Audio State
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Refs for Scroll Containers and Elements
  const containerRef = useRef<HTMLDivElement>(null);
  const transcriptListRef = useRef<HTMLDivElement>(null);

  const transcriptRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Ref for the right panel container (for querying note elements)
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);

  // SVG 连线状态
  const [linkLines, setLinkLines] = useState<{ x1: number; y1: number; x2: number; y2: number }[]>([]);

  // Waveform Data
  const waveformBars = useMemo(() => Array.from({ length: 48 }).map(() => Math.max(20, Math.random() * 100)), []);

  // Format Time Helper
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercent = duration > 0 ? Math.min((currentTime / duration) * 100, 100) : 0;

  // Determine currently playing segment based on timestamp
  const playingSegmentId = useMemo(() => {
    let currentId = null;
    for (let i = 0; i < segments.length; i++) {
      const segTime = parseTime(segments[i].timestamp);
      const nextSegTime = i < segments.length - 1 ? parseTime(segments[i + 1].timestamp) : duration;
      if (currentTime >= segTime && currentTime < nextSegTime) {
        currentId = segments[i].id;
        break;
      }
    }
    return currentId;
  }, [currentTime, segments]);

  // Filter segments based on search query
  const normalizeText = (text: string) =>
    text.toLowerCase().replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '').replace(/\s+/g, ' ').trim();

  const filteredSegments = useMemo(() => {
    if (!searchQuery) return segments;
    const normalizedQ = normalizeText(searchQuery);
    if (!normalizedQ) return segments;
    return segments.filter(seg =>
      normalizeText(seg.text).includes(normalizedQ) ||
      normalizeText(seg.speaker).includes(normalizedQ)
    );
  }, [segments, searchQuery]);
  // Audio playback control
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    if (isPlaying) {
      const playAudio = () => {
        audio.play().catch(e => {
          if (e.name !== 'AbortError') {
            console.error('Audio play failed:', e);
          }
        });
      };

      if (audio.readyState >= 2) {
        playAudio();
      } else {
        audio.addEventListener('canplay', playAudio, { once: true });
      }
    } else {
      audio.pause();
    }
  }, [isPlaying, audioUrl]);

  // Audio playback speed control
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  // Audio time update handler
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioUrl]);

  const handleTranscriptClick = (timeStr: string) => {
    const seconds = parseTime(timeStr);
    setCurrentTime(seconds);
    const audio = audioRef.current;
    if (audio && audioUrl) {
      if (audio.readyState >= 1) {
        audio.currentTime = seconds;
        setIsPlaying(true);
      } else {
        const onLoadedMetadata = () => {
          audio.currentTime = seconds;
          setIsPlaying(true);
        };
        audio.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
      }
    }
  };

  // Speaker name editing - modal approach
  const openSpeakerModal = (segId: string, speaker: string) => {
    setSpeakerModal({ segId, oldName: speaker });
    setSpeakerModalName(speaker);
    setSpeakerModalAll(false);
    setTimeout(() => speakerModalInputRef.current?.select(), 50);
  };

  const closeSpeakerModal = () => {
    setSpeakerModal(null);
    setSpeakerModalName('');
    setSpeakerModalAll(false);
  };

  const saveSpeakerModal = async () => {
    if (!speakerModal || !speakerModalName.trim() || speakerModalName.trim() === speakerModal.oldName) {
      closeSpeakerModal();
      return;
    }
    const { segId, oldName } = speakerModal;
    const newName = speakerModalName.trim();

    if (speakerModalAll) {
      // Update all segments with this speaker name
      setSegments(prev => prev.map(seg =>
        seg.speaker === oldName ? { ...seg, speaker: newName } : seg
      ));
      if (taskId) {
        try { await updateSpeakerName(taskId, oldName, newName); } catch (e) { console.error('Failed to update speaker name:', e); }
      }
    } else {
      // Update only the clicked segment
      setSegments(prev => prev.map(seg =>
        seg.id === segId ? { ...seg, speaker: newName } : seg
      ));
      if (taskId) {
        // segId is "t1", "t2", etc. → sequenceOrder = index (0-based)
        const sequenceOrder = parseInt(segId.replace('t', '')) - 1;
        try { await updateSingleSpeakerName(taskId, sequenceOrder, newName); } catch (e) { console.error('Failed to update speaker name:', e); }
      }
    }

    closeSpeakerModal();
  };

  const handleLinkToggle = (id: string | undefined) => {
    if (!id) return;
    setActiveLinkId(prev => prev === id ? null : id);
  };

  // 计算当前激活连接的所有相关ID（用于两边同时高亮）
  // topLinkedId: 相关度最高的对端ID（只有这个画连线）
  // linkedIds: 所有相关ID（包括 topLinkedId，全部高亮）
  const { linkedIds, topLinkedId } = useMemo(() => {
    const ids = new Set<string>();
    let top: string | null = null;
    if (!activeLinkId) return { linkedIds: ids, topLinkedId: null };

    ids.add(activeLinkId);

    const activeSeg = segments.find(s => s.id === activeLinkId);
    if (activeSeg) {
      const segIndex = parseInt(activeSeg.id.replace('t', '')) - 1;
      const relatedNotes = paragraphMatches
        .filter(m => m.matchedUtteranceIndex === segIndex)
        .sort((a, b) => (b.similarityScore || 0) - (a.similarityScore || 0));
      relatedNotes.forEach(match => {
        ids.add(`n${match.paragraphIndex + 1}`);
      });
      if (relatedNotes.length > 0) {
        top = `n${relatedNotes[0].paragraphIndex + 1}`;
      } else if (activeSeg.linkedNoteId) {
        ids.add(activeSeg.linkedNoteId);
        top = activeSeg.linkedNoteId;
      }
    }

    const activeNote = notes.find(n => n.id === activeLinkId);
    if (activeNote) {
      const noteIndex = parseInt(activeNote.id.replace('n', '')) - 1;
      const relatedSegments = paragraphMatches
        .filter(m => m.paragraphIndex === noteIndex)
        .sort((a, b) => (b.similarityScore || 0) - (a.similarityScore || 0));
      relatedSegments.forEach(match => {
        ids.add(`t${match.matchedUtteranceIndex + 1}`);
      });
      if (relatedSegments.length > 0) {
        top = `t${relatedSegments[0].matchedUtteranceIndex + 1}`;
      } else if (activeNote.linkedTranscriptId) {
        ids.add(activeNote.linkedTranscriptId);
        top = activeNote.linkedTranscriptId;
      }
    }

    return { linkedIds: ids, topLinkedId: top };
  }, [activeLinkId, segments, notes, paragraphMatches]);

  // 点击连线按钮后，自动滚动对端到可视区域
  useEffect(() => {
    if (!activeLinkId || !topLinkedId) return;

    // 延迟一帧，确保高亮状态已渲染
    requestAnimationFrame(() => {
      if (topLinkedId.startsWith('n')) {
        // 对端是右侧纪要，滚动右侧面板
        const nEl = rightPanelRef.current?.querySelector(`[data-block-id="${topLinkedId}"]`) as HTMLElement;
        nEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (topLinkedId.startsWith('t')) {
        // 对端是左侧逐字稿，滚动左侧面板
        const tEl = transcriptRefs.current[topLinkedId];
        tEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }, [activeLinkId, topLinkedId]);

  // 计算所有有匹配关系的 note ID（用于显示连线按钮）
  const matchedNoteIds = useMemo(() => {
    const ids = new Set<string>();
    paragraphMatches.forEach(match => {
      ids.add(`n${match.paragraphIndex + 1}`);
    });
    notes.forEach(note => {
      if (note.linkedTranscriptId) {
        ids.add(note.id);
      }
    });
    return ids;
  }, [paragraphMatches, notes]);

  // 计算所有有匹配关系的 transcript ID（用于左侧显示连线按钮）
  const matchedTranscriptIds = useMemo(() => {
    const ids = new Set<string>();
    paragraphMatches.forEach(match => {
      if (match.matchedUtteranceIndex != null) {
        ids.add(`t${match.matchedUtteranceIndex + 1}`);
      }
    });
    segments.forEach(seg => {
      if (seg.linkedNoteId) {
        ids.add(seg.id);
      }
    });
    return ids;
  }, [paragraphMatches, segments]);

  // 计算连线位置（只连 activeLinkId 和 topLinkedId 之间的一条线）
  // SVG 覆盖层在 mainContentRef 内部（position: absolute），坐标系以 mainContentRef 为原点
  const updateLinkLines = useCallback(() => {
    if (!activeLinkId || !topLinkedId || !mainContentRef.current) {
      setLinkLines([]);
      return;
    }

    const svgOrigin = mainContentRef.current.getBoundingClientRect();

    // 确定哪个是 transcript 侧，哪个是 note 侧
    const tId = activeLinkId.startsWith('t') ? activeLinkId : topLinkedId;
    const nId = activeLinkId.startsWith('n') ? activeLinkId : topLinkedId;

    if (!tId.startsWith('t') || !nId.startsWith('n')) {
      setLinkLines([]);
      return;
    }

    // 左侧：逐字稿卡片
    const tEl = transcriptRefs.current[tId];
    // 右侧：纪要 block
    const nEl = rightPanelRef.current?.querySelector(`[data-block-id="${nId}"]`) as HTMLElement;
    if (!tEl || !nEl) {
      setLinkLines([]);
      return;
    }

    const tRect = tEl.getBoundingClientRect();
    const nRect = nEl.getBoundingClientRect();

    // 检查是否在可视区域内
    if (tRect.bottom < svgOrigin.top || tRect.top > svgOrigin.bottom ||
      nRect.bottom < svgOrigin.top || nRect.top > svgOrigin.bottom) {
      setLinkLines([]);
      return;
    }

    // 左侧端点：逐字稿卡片的右边缘中点（按钮在 absolute -right-2 位置，用卡片右边缘即可）
    const x1 = tRect.right - svgOrigin.left;
    const y1 = tRect.top + tRect.height / 2 - svgOrigin.top;

    // 右侧端点：纪要 block 高亮框（selectionWrapper）的左边缘中点
    const selEl = nEl.querySelector(`[data-selection-wrapper]`) as HTMLElement;
    const targetRect = selEl ? selEl.getBoundingClientRect() : nRect;
    const x2 = targetRect.left - svgOrigin.left;
    const y2 = targetRect.top + targetRect.height / 2 - svgOrigin.top;

    setLinkLines([{ x1, y1, x2, y2 }]);
  }, [activeLinkId, topLinkedId]);

  // 当 activeLinkId 变化或滚动时更新连线
  useEffect(() => {
    updateLinkLines();

    const transcriptList = transcriptListRef.current;
    const rightPanel = rightPanelRef.current;

    // 监听两侧滚动
    const handleScroll = () => updateLinkLines();
    transcriptList?.addEventListener('scroll', handleScroll);
    rightPanel?.addEventListener('scroll', handleScroll, true); // capture 以捕获内部滚动

    // 监听右侧面板 DOM 变化（原始版/编辑版切换导致布局变化）
    let observer: MutationObserver | null = null;
    if (rightPanel) {
      observer = new MutationObserver(() => {
        requestAnimationFrame(updateLinkLines);
      });
      observer.observe(rightPanel, { childList: true, subtree: true });
    }

    return () => {
      transcriptList?.removeEventListener('scroll', handleScroll);
      rightPanel?.removeEventListener('scroll', handleScroll, true);
      observer?.disconnect();
    };
  }, [updateLinkLines]);

  return (
    <div className="flex flex-col h-screen bg-surface text-brand-900 overflow-hidden font-sans selection:bg-surface-border selection:text-brand-800" ref={containerRef}>
      {/* Hidden Audio Element */}
      {audioUrl && (
        <audio ref={audioRef} src={audioUrl} preload="metadata" />
      )}

      {/* --- Global Header --- */}
      <header className="h-16 bg-surface-light/90 backdrop-blur-xl border-b border-surface-border flex items-center justify-between px-6 z-40 shrink-0 sticky top-0 shadow-sm">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-surface rounded-full text-ink-muted hover:text-brand-900 transition-colors group">
            <ArrowLeft size={20} className="group-hover:-translate-x-0.5 transition-transform" />
          </button>
          <div className="flex flex-col justify-center">
            <h1 className="font-serif font-bold text-brand-900 text-xl tracking-tight leading-none mb-1">{title}</h1>
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-ink-secondary">
              <span className="bg-surface-border text-brand-700 px-1.5 py-0.5 rounded border border-surface-border">{dataLoading ? t.workspace.loading : t.workspace.processed}</span>
              <span className="opacity-60">{date}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Language Toggle Button */}
          <button
            onClick={() => setLanguage(language === 'zh' ? 'en' : 'zh')}
            className="h-9 w-9 flex items-center justify-center text-ink-muted hover:text-brand-900 hover:bg-surface rounded-full transition-colors"
            title={language === 'zh' ? t.workspace.switchToEnglish : t.workspace.switchToChinese}
          >
            <Globe size={18} />
          </button>

          <button className="h-9 w-9 flex items-center justify-center text-ink-muted hover:text-brand-900 hover:bg-surface rounded-full transition-colors">
            <MoreHorizontal size={20} />
          </button>
        </div>
      </header>

      {/* --- Main Content --- */}
      <div className="flex-1 flex overflow-hidden relative z-10" ref={mainContentRef}>
        {dataLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-ink-muted">
              <Loader2 size={28} className="animate-spin" />
              <span className="text-sm">{t.workspace.loadingEllipsis}</span>
            </div>
          </div>
        ) : dataError ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-ink-muted">
              <span className="text-sm text-red-500">{dataError}</span>
              <button onClick={onBack} className="text-xs text-ink-secondary hover:text-brand-900 underline">
                {t.workspace.backButton}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* 左侧 - 模板中心模式下显示模板编辑器，否则显示逐字稿 */}
            {isTemplateCenter ? (
              <div className="flex-1 bg-surface overflow-hidden" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <SchemaEditorWrapper meetingNotes={notes} originalNotes={originalNotes} mode="edit" taskTitle={title} onApplyTemplate={handleApplyTemplate} appliedSchemaType={taskData?.appliedSchemaType} appliedSchemaId={taskData?.appliedSchemaId} onBack={() => setIsTemplateCenter(false)} savedEditedNotes={savedEditedNotes} />
              </div>
            ) : (
              <>
                <div className="flex flex-col border-r border-surface-border bg-surface-light relative shadow-[5px_0_30px_-15px_rgba(0,0,0,0.03)] transition-all duration-300 ease-in-out z-20 w-[40%]">
                  <>
                    {/* 音频播放器 - 仅在音频来源时显示 */}
                    {sourceType === 'audio' && (
                      <div className="bg-surface-light border-b border-surface-border p-5 flex flex-col gap-4 shrink-0 z-20">
                        <div className="relative h-16 w-full flex items-center group cursor-pointer overflow-hidden rounded-xl bg-surface border border-surface-border px-4 select-none">
                          <div className="absolute inset-0 flex items-center justify-between px-6 py-3">
                            {waveformBars.map((height, i) => {
                              const percent = (i / (waveformBars.length - 1)) * 100;
                              const isPlayed = percent <= progressPercent;
                              return (
                                <div
                                  key={i}
                                  className={`w-1.5 rounded-full transition-colors duration-150 ${isPlayed ? 'bg-brand-900' : 'bg-surface-divider'}`}
                                  style={{ height: `${height * 0.8}%` }}
                                ></div>
                              );
                            })}
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 h-1 bg-surface-border">
                            <div
                              className="h-full bg-brand-900 transition-all duration-100 ease-linear relative"
                              style={{ width: `${progressPercent}%` }}
                            >
                              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-brand-900 rounded-full shadow-sm scale-0 group-hover:scale-100 transition-transform"></div>
                            </div>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max={duration || 100}
                            value={currentTime}
                            onChange={(e) => {
                              const newTime = parseInt(e.target.value);
                              setCurrentTime(newTime);
                              if (audioRef.current) {
                                audioRef.current.currentTime = newTime;
                              }
                            }}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                          />
                        </div>

                        <div className="flex items-center justify-between relative px-1">
                          <div className="flex items-baseline gap-1.5 w-32">
                            <span className="text-2xl font-mono font-bold text-brand-900 tabular-nums tracking-tight">{formatTime(currentTime)}</span>
                            <span className="text-xs text-ink-muted font-medium font-sans tabular-nums">/ {formatTime(duration)}</span>
                          </div>
                          <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-5">
                            <button className="text-ink-muted hover:text-brand-900 transition-colors p-2 rounded-full hover:bg-surface" onClick={() => {
                              const newTime = Math.max(0, currentTime - 15);
                              setCurrentTime(newTime);
                              if (audioRef.current) audioRef.current.currentTime = newTime;
                            }}>
                              <RotateCcw size={20} strokeWidth={2} />
                            </button>
                            <button
                              onClick={() => setIsPlaying(!isPlaying)}
                              className="w-14 h-14 flex items-center justify-center bg-brand-900 text-white rounded-full hover:scale-105 transition-all shadow-btn-green active:scale-95 ring-4 ring-surface-light"
                            >
                              {isPlaying ? <Pause size={24} fill="currentColor" /> : <Play size={24} fill="currentColor" className="ml-1" />}
                            </button>
                            <button className="text-ink-muted hover:text-brand-900 transition-colors p-2 rounded-full hover:bg-surface" onClick={() => {
                              const newTime = Math.min(duration || 0, currentTime + 15);
                              setCurrentTime(newTime);
                              if (audioRef.current) audioRef.current.currentTime = newTime;
                            }}>
                              <RotateCw size={20} strokeWidth={2} />
                            </button>
                          </div>
                          <div className="w-32 flex justify-end">
                            <button
                              onClick={() => setPlaybackSpeed(prev => prev === 1 ? 1.5 : prev === 1.5 ? 2 : 1)}
                              className="px-3 py-1.5 rounded-lg text-xs font-bold text-ink-dark border border transparent hover:bg-surface transition-colors"
                            >
                              {playbackSpeed}x Speed
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="px-5 py-3 border-b border-surface-border bg-surface-light sticky top-0 z-10">
                      <div className="relative group">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted group-focus-within:text-brand-900 transition-colors" size={14} />
                        <input
                          type="text"
                          placeholder={t.workspace.searchTranscript}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-9 pr-4 py-2 bg-surface border border-transparent focus:bg-white focus:border-surface-divider rounded-lg text-sm text-brand-900 placeholder-ink-muted outline-none transition-all shadow-sm"
                        />
                      </div>
                    </div>

                    <div
                      ref={transcriptListRef}
                      className="flex-1 overflow-y-auto p-4 space-y-3 relative bg-surface-light custom-scrollbar"
                    >
                      {filteredSegments.length > 0 ? filteredSegments.map(seg => {
                        const isLinkedActive = linkedIds.has(seg.id);
                        const isPlayingActive = playingSegmentId === seg.id;
                        return (
                          <div
                            key={seg.id}
                            ref={el => { transcriptRefs.current[seg.id] = el; }}
                            onClick={() => sourceType === 'audio' && handleTranscriptClick(seg.timestamp)}
                            className={`flex gap-3 p-3 rounded-xl transition-all duration-300 relative group ${sourceType === 'audio' ? 'cursor-pointer' : ''}
                                ${isPlayingActive ? 'bg-surface' : ''}
                                ${isLinkedActive ? 'bg-surface-border/50 ring-1 ring-brand-700' : ''}
                                ${!isPlayingActive && !isLinkedActive ? 'hover:bg-gray-50' : ''}
                                `}
                          >
                            <div className={`w-6 h-6 rounded flex-shrink-0 flex items-center justify-center text-[10px] font-bold border shadow-sm transition-colors mt-0.5
                                    ${isPlayingActive ? 'bg-brand-900 text-white border-brand-900' :
                                'bg-white text-ink-secondary border-surface-border'}`}>
                              {isPlayingActive ? <Mic size={10} className="animate-pulse" /> : seg.speaker.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span
                                  onClick={(e) => { e.stopPropagation(); openSpeakerModal(seg.id, seg.speaker); }}
                                  className={`font-bold text-[10px] uppercase tracking-wide cursor-pointer hover:bg-surface-border px-1.5 py-0.5 rounded transition-colors ${isLinkedActive || isPlayingActive ? 'text-brand-900' : 'text-ink-secondary'}`}
                                  title={t.workspace.clickEditSpeaker}
                                >
                                  {seg.speaker}
                                </span>
                                {sourceType === 'audio' && (
                                  <span className={`text-[10px] font-mono ${isLinkedActive ? 'text-ink-dark' : 'text-ink-muted'}`}>{seg.timestamp}</span>
                                )}
                              </div>
                              <p className={`leading-relaxed text-sm ${isLinkedActive || isPlayingActive ? 'text-brand-900 font-medium' : 'text-ink-dark'}`}>
                                {seg.text}
                              </p>
                            </div>
                            {matchedTranscriptIds.has(seg.id) && (
                              <button
                                data-link-btn={seg.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleLinkToggle(seg.id);
                                }}
                                className={`absolute -right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center shadow-md border-2 z-20 transition-all duration-300
                                        ${isLinkedActive
                                    ? 'bg-brand-900 text-white border-white scale-110'
                                    : 'bg-white text-ink-muted border-surface-border opacity-0 group-hover:opacity-100 hover:text-ink-dark hover:border-ink-dark'
                                  }`}
                              >
                                <LinkIcon size={10} strokeWidth={2.5} />
                              </button>
                            )}
                          </div>
                        );
                      }) : (
                        <div className="flex flex-col items-center justify-center h-full text-ink-muted py-10 opacity-60">
                          <Search size={24} className="mb-2" />
                          <span className="text-xs font-medium">No results found</span>
                        </div>
                      )}
                    </div>
                  </>
                </div>

                {/* 右侧区域 - 仅预览纪要 */}
                <div className="flex-1 bg-surface overflow-hidden" ref={rightPanelRef}>
                  <SchemaEditorWrapper meetingNotes={notes} originalNotes={originalNotes} mode="preview" onNotesChange={setNotes} taskId={taskId} activeLinkId={activeLinkId} linkedIds={linkedIds} onLinkToggle={handleLinkToggle} matchedNoteIds={matchedNoteIds} topLinkedId={topLinkedId} taskTitle={title} schemaVersion={schemaVersion} onApplyTemplate={handleApplyTemplate} appliedSchemaType={taskData?.appliedSchemaType} appliedSchemaId={taskData?.appliedSchemaId} isTemplateCenter={isTemplateCenter} onToggleTemplateCenter={() => setIsTemplateCenter(!isTemplateCenter)} savedEditedNotes={savedEditedNotes} />
                </div>

                {/* SVG 连线覆盖层 */}
                {linkLines.length > 0 && (
                  <svg
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'none',
                      zIndex: 50,
                    }}
                  >
                    {linkLines.map((line, i) => {
                      const dx = Math.abs(line.x2 - line.x1);
                      const cp = Math.max(40, dx * 0.4);
                      return (
                        <path
                          key={i}
                          d={`M ${line.x1} ${line.y1} C ${line.x1 + cp} ${line.y1}, ${line.x2 - cp} ${line.y2}, ${line.x2} ${line.y2}`}
                          fill="none"
                          stroke="#1a2e05"
                          strokeWidth="2.5"
                          strokeDasharray="6 4"
                          opacity="0.6"
                        />
                      );
                    })}
                  </svg>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Speaker Edit Modal */}
      {speakerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={closeSpeakerModal}>
          <div className="absolute inset-0 bg-black/20" />
          <div
            className="relative bg-white rounded-2xl shadow-xl p-5 w-72 animate-in fade-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-xs font-semibold text-brand-900 mb-3">
              {t.workspace.editSpeaker}
            </div>
            <input
              ref={speakerModalInputRef}
              type="text"
              value={speakerModalName}
              onChange={(e) => setSpeakerModalName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveSpeakerModal();
                if (e.key === 'Escape') closeSpeakerModal();
              }}
              className="w-full text-sm text-brand-900 bg-surface border border-surface-border rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-brand-700 focus:border-brand-700 mb-3"
              placeholder={t.workspace.enterSpeakerName}
            />
            <label
              className="flex items-center gap-2.5 cursor-pointer select-none mb-4 group"
              onClick={() => setSpeakerModalAll(prev => !prev)}
            >
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${speakerModalAll
                  ? 'border-brand-700 bg-brand-700'
                  : 'border-gray-300 bg-white group-hover:border-gray-500'
                }`}>
                {speakerModalAll && (
                  <div className="w-1.5 h-1.5 rounded-full bg-white" />
                )}
              </div>
              <span className="text-xs text-ink-dark">
                {`${t.workspace.renameAllSpeaker} "${speakerModal.oldName}"`}
              </span>
            </label>
            <div className="flex gap-2">
              <button
                onClick={closeSpeakerModal}
                className="flex-1 text-xs py-1.5 rounded-lg border border-surface-border text-ink-secondary hover:bg-surface transition-colors"
              >
                {t.workspace.cancel}
              </button>
              <button
                onClick={saveSpeakerModal}
                className="flex-1 text-xs py-1.5 rounded-lg bg-brand-900 text-white hover:bg-brand-800 transition-colors"
              >
                {t.workspace.confirm}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
